import { useTranslation } from 'react-i18next';

// Utils
import cn from '../../../../utils/cn';
import { normalizeUrl } from '../../../../utils/url';

// Styles
import styles from './DirectEditWrapper.module.css';
import directEditCommon from '../DirectEditCommon.module.css';

// Domain
import useTableDirectEdit from '../../../../domain/tableDirectEdit/useTableDirectEdit';
import {
  buildSelectClassOptimisticPatch,
  getCellDateStringValue,
  isCellDisplayValueEmpty,
} from '../../../../domain/tableDirectEdit/tableDirectEditUseCase';

// Components
import DirectEditLoadingCell from '../DirectEditLoadingCell/DirectEditLoadingCell';
import TextField from '../../Fields/TextField/TextField';
import TextareaField from '../../Fields/TextareaField/TextareaField';
import ColorField from '../../Fields/ColorField/ColorField';
import TimeField from '../../Fields/TimeField/TimeField';
import DateTimeField from '../../Fields/DateTimeField/DateTimeField';
import SelectClassField from '../../Fields/SelectClassField/SelectClassField';
import DirectEditBoolean from '../DirectEditBoolean/DirectEditBoolean';
import DirectEditActionsRender from '../DirectEditActionsRender/DirectEditActionsRender';
import { getDirectEditShellProps } from '../getDirectEditShellClassName';
import DirectEditValueSlot from '../DirectEditValueSlot/DirectEditValueSlot';

/**
 * DirectEditWrapper component that wraps table cells to enable inline editing
 * @param {object} props
 * @param {React.ReactNode} props.children - The cell component to wrap
 * @param {TableCellValueObjectCustom} props.cellValue - Cell value with directEdit config
 * @param {ApiManagerTableColumns} props.column - Column configuration with validation
 * @param {(editing: boolean) => void} [props.onEditingChange] - Optional; used by CellHoverPreviewWrapper to suppress hover
 * @param {boolean} [props.activateOnMount=false] - Open the editor immediately (active cell after a static activate gesture)
 * @returns {JSX.Element}
 */
function DirectEditWrapper({
  children,
  cellValue,
  column,
  onEditingChange,
  activateOnMount = false,
}) {
  const { t } = useTranslation();
  const {
    // State
    isEditing,
    editValue,
    validationError,
    isPendingSave,
    hasDirectEdit,
    directEditType,

    // Refs
    containerRef,

    // Handlers
    handleSave,
    handleBlur,
    handleKeyDown,
    handleClick,
    handleDoubleClick,
    handleBooleanClick,
    handleChange,
    handleCancel,
  } = useTableDirectEdit({
    cellValue, column, onEditingChange, activateOnMount,
  });

  if (isPendingSave) {
    return (
      <DirectEditLoadingCell cellValue={cellValue}>
        {children}
      </DirectEditLoadingCell>
    );
  }

  const { className: shellClassName, style: shellStyle } = getDirectEditShellProps(cellValue);
  const showLinkIcon = isCellDisplayValueEmpty(cellValue);

  // Handle actionChoice type - show actions popover on click
  if (directEditType === 'actionChoice' && hasDirectEdit) {
    return (
      <DirectEditActionsRender
        cellValue={cellValue}
        column={column}
        onEditingChange={onEditingChange}
        initialOpen={activateOnMount}
      >
        {children}
      </DirectEditActionsRender>
    );
  }

  // Handle boolean type - toggle on click
  if (directEditType === 'boolean' && hasDirectEdit) {
    return (
      <DirectEditBoolean
        cellValue={cellValue}
        onClick={handleBooleanClick}
        isPendingSave={isPendingSave}
      />
    );
  }

  if (!hasDirectEdit || !isEditing) {
    // If basicUrl is present and no directEdit, handle click to open URL in new tab
    const hasBasicUrl = cellValue?.basicUrl != null;

    if (hasBasicUrl && !hasDirectEdit) {
      const handleBasicUrlClick = () => {
        const normalizedUrl = normalizeUrl(cellValue.basicUrl);
        window.open(normalizedUrl, '_blank');
      };

      return (
        <div className={shellClassName} style={shellStyle}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleBasicUrlClick();
            }}
            className={cn([
              directEditCommon['direct-edit-value-control'],
              directEditCommon['direct-edit-value-control-link'],
            ])}
          >
            <DirectEditValueSlot action={showLinkIcon ? 'link' : null}>{children}</DirectEditValueSlot>
          </button>
        </div>
      );
    }

    if (hasDirectEdit) {
      // Single-click activation for most types; double-click for text, textarea, date, time, datetime
      return (
        <div className={shellClassName} style={shellStyle}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleDoubleClick();
            }}
            className={cn([
              directEditCommon['direct-edit-value-control'],
              directEditCommon['direct-edit-value-control-editable'],
            ])}
          >
            <DirectEditValueSlot action={null}>{children}</DirectEditValueSlot>
          </button>
        </div>
      );
    }

    return <div className={shellClassName} style={shellStyle}>{children}</div>;
  }

  // Handle other types - show input after activation (click or double-click by type)
  /**
   * Render the appropriate input field based on directEditType
   * @returns {JSX.Element | null}
   */
  const renderInputField = () => {
    const errorMessage = t(validationError || '', { maxLength: column.validation?.maxLength });
    const containerClassName = styles['direct-edit-input-container'];

    switch (directEditType) {
      case 'text':
        return (
          <div className={containerClassName} ref={containerRef}>
            <TextField
              value={editValue}
              onChange={handleChange}
              disabled={isPendingSave}
              error={errorMessage}
              className={styles['direct-edit-input']}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
          </div>
        );

      case 'textarea':
        return (
          <div className={containerClassName} ref={containerRef}>
            <TextareaField
              value={editValue}
              onChange={handleChange}
              disabled={isPendingSave}
              error={errorMessage}
              className={styles['direct-edit-input']}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              theme="table"
            />
          </div>
        );

      case 'color':
        return (
          <div
            className={cn([
              containerClassName,
              styles['direct-edit-color-container'],
            ])}
            ref={containerRef}
          >
            <ColorField
              value={editValue}
              onChange={handleChange}
              disabled={isPendingSave}
              error={errorMessage}
              className={styles['direct-edit-input']}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              theme="table"
              initialOpen={isEditing}
            />
          </div>
        );

      case 'time':
        return (
          <div className={containerClassName} ref={containerRef}>
            <TimeField
              value={editValue}
              onChange={handleChange}
              disabled={isPendingSave}
              error={errorMessage}
              className={styles['direct-edit-input']}
              theme="table"
              withQuarterHourDropdown
              onQuarterHourSelect={(selectedValue) => handleSave(selectedValue)}
              onValidatedCommit={(committedValue) => handleSave(committedValue)}
              onCommitInvalid={handleCancel}
            />
          </div>
        );

      case 'date': {
        const dateFieldValue = typeof editValue === 'string'
          ? editValue
          : getCellDateStringValue(cellValue);

        return (
          <div className={containerClassName} ref={containerRef}>
            <DateTimeField
              type="date"
              value={dateFieldValue}
              onChange={handleChange}
              disabled={isPendingSave}
              error={errorMessage}
              className={styles['direct-edit-input']}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              theme="table"
              withCalendarDropdown
              onDaySelect={(selectedValue) => handleSave(selectedValue)}
            />
          </div>
        );
      }

      case 'datetime':
        return (
          <div className={containerClassName} ref={containerRef}>
            <DateTimeField
              type="datetime-local"
              value={editValue}
              onChange={handleChange}
              disabled={isPendingSave}
              error={errorMessage}
              className={styles['direct-edit-input']}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              theme="table"
            />
          </div>
        );

      case 'selectClass': {
        const selectClassConfig = cellValue?.directEdit?.selectClass;

        return (
          <div className={containerClassName} ref={containerRef}>
            <SelectClassField
              value={editValue || null}
              onChange={(newValue, selectedOption) => handleSave(
                newValue || '',
                buildSelectClassOptimisticPatch(cellValue, {
                  label: selectedOption?.label ?? '',
                  color: selectedOption?.color ?? null,
                }),
              )}
              onBlur={handleBlur}
              onEscapeWhenMenuClosed={handleCancel}
              disabled={isPendingSave}
              error={errorMessage}
              className={styles['direct-edit-input']}
              selectOptionsUrl={selectClassConfig?.selectOptionsUrl || ''}
              theme="table"
              initialOpen={isEditing}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  const inputField = renderInputField();
  if (!inputField) {
    return <div>{children}</div>;
  }
  return (
    /*
     * Event barrier only: `onClick` runs `stopPropagation()` so clicks inside the inline editor
     * (inputs, dropdowns, calendar, etc.) do not bubble to the table cell / row (e.g. row selection).
     *
     * This `<div>` is not an interactive widget: focus and keyboard handling stay on the inner
     * fields. Using `<button type="button">`, `role="button"`, or `tabIndex` here would be wrong
     * semantics and could steal focus or confuse assistive tech vs. the actual form controls.
     *
     * jsx-a11y flags click handlers on non-interactive elements as if this were a custom control;
     * the handler here does not implement an interactive surface, only stops event bubbling.
     */
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn([shellClassName, styles['direct-edit-editing-root']])}
      style={shellStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {inputField}
    </div>
  );
}

export default DirectEditWrapper;
